import { useEffect, useRef, useState, useCallback } from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { useCart } from "@/lib/cart"
import type { Hotel } from "@/types/travel"
import { RoomSelectionModal } from "@/components/hotels/RoomSelectionModal"
import { getHotelDetails, type HotelRoomOption } from "@/lib/hotels-api"
import type { HotelSearchParams as BackendHotelSearchParams } from "@/lib/hotels-api"
import { calculatePricing, type PricingResult, type PricingRules } from "@/lib/subscription-rules"
import { useSubscriptionRules } from "@/hooks/useSubscriptionRules"

// ============================================
// Guest Rating Helpers
// ============================================

/**
 * Only values > 5 are treated as review scores (0-10 scale).
 * Values 1-5 are star ratings and should NOT be shown as guest rating.
 */
export function isReviewScore(value: number): boolean {
  return value > 5 && value <= 10
}

export function getGuestRatingLabel(score: number): string {
  if (score >= 9) return "Exceptional"
  if (score >= 8.5) return "Superb"
  if (score >= 8) return "Excellent"
  if (score >= 7) return "Very good"
  if (score >= 6) return "Good"
  return "Pleasant"
}

// ============================================
// Types
// ============================================

interface HotelCardProps {
  hotel: Hotel
  onSelect: (hotel: Hotel) => void
  checkin?: string
  checkout?: string
  guests?: Array<{ adults: number; children: number[] }>
  /**
   * Kept for backwards-compatibility with consumers that pass the
   * full hotel array (e.g. for "best value" math). Currently unused
   * by the new design — the featured variant is decided by the parent
   * and flowed through `featured` instead.
   */
  allHotels?: Hotel[]
  /**
   * VOY-488: when true, render the gold "VOY PICK" pill + tinted
   * `.hotel-card.featured` border. Parents typically pass
   * `index === 0` for the top-recommended result.
   */
  featured?: boolean
}

// ============================================
// Component
// ============================================

export function HotelCard({ hotel, onSelect, checkin, checkout, guests, featured = false }: HotelCardProps) {
  const { addToCart } = useCart();
  const [showRoomModal, setShowRoomModal] = useState(false);

  // Image carousel state
  const [allImages, setAllImages] = useState<string[]>(() => {
    return (hotel.images || []).filter(img => img && typeof img === 'string' && img.startsWith('http'));
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [displayPrice, setDisplayPrice] = useState<number | null>(
    (hotel.price && (hotel as any).price.amount && (hotel as any).price.amount > 0)
      ? (hotel as any).price.amount
      : hotel.pricePerNight || null
  );
  const [displayTotalPrice, setDisplayTotalPrice] = useState<number | null>(
    hotel.totalPrice || (hotel.price as any)?.totalPrice || null
  );
  const [displayNights, setDisplayNights] = useState<number | null>(
    hotel.nights || (hotel.price as any)?.nights || null
  );
  const [displayLocation, setDisplayLocation] = useState<string>(hotel.coordinates?.address || hotel.location);
  const [displayAmenities, setDisplayAmenities] = useState<string[]>(hotel.amenities || []);
  // Bug fix: don't fall back to hotel.rating (review score 0-10) for star display
  const [displayStarRating, setDisplayStarRating] = useState<number | undefined>(hotel.starRating);
  const [displayName, setDisplayName] = useState<string>(hotel.name || 'Hotel');
  // Guest rating: only show if it's a real review score (> 5 on 0-10 scale)
  const [displayGuestRating, setDisplayGuestRating] = useState<number | null>(() => {
    const r = hotel.rating;
    return typeof r === 'number' && isReviewScore(r) ? r : null;
  });

  // Policy badges state (loaded lazily from room details)
  const [policyBadges, setPolicyBadges] = useState<{
    freeCancellation: boolean;
    breakfastIncluded: boolean;
    payAtHotel: boolean;
    nonRefundable: boolean;
    lowStock: number | null;
  }>({
    freeCancellation: false,
    breakfastIncluded: false,
    payAtHotel: false,
    nonRefundable: false,
    lowStock: null,
  });

  // VOY-488: featured ranking ("VOY PICK") now flows through the
  // `featured` prop from the parent rather than being computed here.
  // Drops the prior heuristic-driven "Best Value" banner in favor of
  // a single design-driven highlight.

  // Image carousel handlers
  const handlePrevImage = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev === 0 ? allImages.length - 1 : prev - 1));
  }, [allImages.length]);

  const handleNextImage = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => (prev === allImages.length - 1 ? 0 : prev + 1));
  }, [allImages.length]);

  // Intersection observer for lazy loading
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { rootMargin: '100px 0px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => {
      try { observer.unobserve(el); } catch { /* ignore */ }
      try { observer.disconnect(); } catch { /* ignore */ }
    };
  }, []);

  // Lazy-load details when card becomes visible
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (isVisible && (allImages.length === 0 || displayPrice === null) && checkin && checkout && guests) {
        try {
          await new Promise(r => setTimeout(r, Math.floor(Math.random() * 300)));
          const details = await getHotelDetails(String(hotel.id), {
            checkin,
            checkout,
            guests: guests as BackendHotelSearchParams['guests'],
            residency: 'US',
            language: 'en',
            currency: 'USD',
          } as unknown as BackendHotelSearchParams);
          if (cancelled) return;

          // Images — update carousel
          if (details.images) {
            const validImages = details.images.filter(img => img && typeof img === 'string' && img.startsWith('http'));
            if (validImages.length > 0) {
              setAllImages(validImages);
              if (currentImageIndex >= validImages.length) setCurrentImageIndex(0);
            }
          }

          // Price
          if (displayPrice === null && typeof details.price === 'number' && details.price > 0) {
            setDisplayPrice(details.price);
          }
          if (displayTotalPrice === null && (details as any).totalPrice) {
            setDisplayTotalPrice((details as any).totalPrice);
          }
          if (displayNights === null && (details as any).nights) {
            setDisplayNights((details as any).nights);
          }

          // Location, amenities, ratings
          if (details.location && details.location !== displayLocation) {
            setDisplayLocation(details.location);
          }
          if (Array.isArray(details.amenities) && details.amenities.length > 0) {
            setDisplayAmenities(details.amenities.slice(0, 8));
          }
          if (typeof details.starRating === 'number') {
            setDisplayStarRating(details.starRating as number);
          }
          // Only set guest rating if it's a real review score (> 5 on 0-10 scale)
          if (typeof details.rating === 'number' && isReviewScore(details.rating)) {
            setDisplayGuestRating(details.rating);
          }
          if ((!hotel.name || hotel.name === 'Unknown' || hotel.name === 'Hotel' || displayName === 'Hotel') && details.name) {
            setDisplayName(details.name);
          }

          // Extract policy badges from room data
          if (details.rooms && details.rooms.length > 0) {
            const firstRoom = details.rooms[0];
            const cancelPolicy = (firstRoom.cancellationPolicy || '').toLowerCase();
            const hasFreeCancel = cancelPolicy.includes('free') || cancelPolicy.includes('refundable');
            const isNonRefundable = cancelPolicy.includes('non-refundable') || cancelPolicy.includes('nonrefundable');
            const hasBreakfast = (firstRoom.mealPlan || '').toLowerCase().includes('breakfast');
            const minAvailability = Math.min(...details.rooms.map(r => r.available ?? 999).filter(a => a > 0));

            setPolicyBadges({
              freeCancellation: hasFreeCancel && !isNonRefundable,
              breakfastIncluded: hasBreakfast,
              payAtHotel: false,
              nonRefundable: isNonRefundable,
              lowStock: minAvailability <= 5 ? minAvailability : null,
            });
          }
        } catch { /* ignore errors fetching hotel details */ }
      }
    }
    load();
    return () => { cancelled = true };
  }, [hotel.id, checkin, checkout, JSON.stringify(guests), isVisible]);

  // Filter valid amenities. `remainingCount` is computed AFTER perkChips
  // is built (below) — when policy badges fill chip slots they push
  // amenities off the visible set, and the "+N more" count needs to
  // reflect what was actually hidden, not just the >3-slice tail.
  const validAmenities = displayAmenities.filter(a => a && a.trim());

  // VOY-488: dual-pricing math (std strikethrough + member) sourced
  // from the shared `calculatePricing` helper, same approach as the
  // flight card (VOY-487). Strikethrough always reflects the marketing
  // non-member standard rate regardless of viewer state.
  const { config: hotelConfig } = useSubscriptionRules({ product: 'hotel', hotelId: hotel.id });
  const nonMemberRules: PricingRules = {
    tier: 'free',
    markupPercentage: hotelConfig.markupPercentage,
    canHaveSpecialPrice: false,
    paywallEnabled: true,
    ticketLimit: null,
    beneficiaryPolicy: 'any',
  };
  const pricing = displayPrice && displayPrice > 0
    ? calculatePricing(displayPrice, nonMemberRules, hotelConfig)
    : null;
  const showSavings = pricing && pricing.savings > 0.005;
  const fmtMoney = (n: number) => `$${Math.round(n).toLocaleString()}`;

  // Star rating "★★★★☆" string with empty stars showing at 25% opacity.
  const starsFilled = Math.max(0, Math.min(5, displayStarRating ?? 0));
  const starsEmpty = 5 - starsFilled;

  const handleCardClick = () => {
    if (!hotel?.id) return;
    if (!checkin || !checkout || !guests || !Array.isArray(guests) || guests.length === 0) {
      onSelect(hotel);
      return;
    }
    setShowRoomModal(true);
  };

  const handlePerNightCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCardClick();
  };

  const currentImage = allImages[currentImageIndex] || null;

  // The .lbl line uses whatever the API returned in `location` verbatim
  // — usually "District · City" or "Address, City" depending on
  // supplier. No client-side parsing.
  const locationLabel = displayLocation || '';

  // Perks: prefer policy-derived chips (cancellation, breakfast) when
  // present, else fall back to the first amenity strings. The design
  // shows ~3 chips max.
  const perkChips: string[] = [];
  if (policyBadges.freeCancellation) perkChips.push('Free cancellation');
  if (policyBadges.breakfastIncluded) perkChips.push('Breakfast included');
  for (const a of validAmenities) {
    if (perkChips.length >= 3) break;
    if (!perkChips.includes(a)) perkChips.push(a);
  }
  // Count amenities NOT shown — policy-chip names that aren't part of
  // the raw amenities list don't count as "shown amenities", so the
  // +N more badge stays honest. Example: 6 amenities + both policy
  // chips firing → perkChips = [Free cancel, Breakfast, a1]; shown
  // amenities = 1; remaining = 5 (not 3 as the prior slice-based math
  // would have claimed).
  const shownAmenityCount = perkChips.filter((c) => validAmenities.includes(c)).length;
  const remainingCount = Math.max(0, validAmenities.length - shownAmenityCount);

  // Free-cancellation deadline line under the save-tag — best-effort
  // wording when policy is known. Falls back to no suffix.
  const cancelSuffix = policyBadges.freeCancellation ? ' · cancel free' : '';

  return (
    <>
      <article
        ref={containerRef}
        className={`hotel-card${featured ? ' featured' : ''}`}
        onClick={handleCardClick}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        aria-label={`${displayName}${displayStarRating ? `, ${displayStarRating} stars` : ''}${pricing ? `, ${fmtMoney(pricing.specialPrice)} per night as member` : ''}`}
      >
        {featured && <span className="best-pill">VOY PICK</span>}

        {/* Left: image well */}
        <div className="hc-img">
          {currentImage ? (
            <img
              src={currentImage}
              alt={displayName}
              loading="lazy"
              onError={() => {
                // Remove broken image — functional updates avoid stale closure
                setCurrentImageIndex(prevIdx => {
                  setAllImages(prevImgs => prevImgs.filter((_, i) => i !== prevIdx));
                  return prevIdx > 0 ? prevIdx - 1 : 0;
                });
              }}
            />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: 'var(--v6-fg-muted)', fontSize: 12 }}>
              Hotel photo
            </div>
          )}

          {/* Carousel controls — visible on hover */}
          {allImages.length > 1 && (
            <>
              <button type="button" className="hc-carousel-btn prev" onClick={handlePrevImage} aria-label="Previous image">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" className="hc-carousel-btn next" onClick={handleNextImage} aria-label="Next image">
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}

          {/* Photo-count pill — only when >1 to avoid "× 1 photos". */}
          {allImages.length > 1 && (
            <span className="img-badge">× {allImages.length} photos</span>
          )}
        </div>

        {/* Right: body */}
        <div className="hc-body">
          <div className="hc-h">
            <div style={{ flex: 1, minWidth: 0 }}>
              {starsFilled > 0 && (
                <div className="stars" aria-label={`${starsFilled} star rating`}>
                  {'★'.repeat(starsFilled)}
                  {starsEmpty > 0 && <span className="star-empty">{'★'.repeat(starsEmpty)}</span>}
                </div>
              )}
              <div className="hotel-name">{displayName}</div>
              {locationLabel && (
                <div className="lbl">{locationLabel}</div>
              )}
              {/* VOY-466: Match-My-Taste pill + tooltip. Renders iff
                  the backend returned a match_score for this hotel
                  (i.e. the search was made with personalize=true on
                  an authed user). Hidden when the user toggled the
                  flag off OR they're anon. */}
              {typeof hotel.match_score === 'number' && !hotel.excluded && (
                <div
                  className="hc-match-pill"
                  title={
                    Array.isArray(hotel.match_reasons) && hotel.match_reasons.length > 0
                      ? `Match my taste:\n• ${hotel.match_reasons.join('\n• ')}`
                      : 'Personalised match for you'
                  }
                  aria-label={`Match score ${Math.round(hotel.match_score * 100)} percent`}
                >
                  <span className="hc-match-pill__icon" aria-hidden="true">✦</span>
                  <span className="hc-match-pill__score">
                    {Math.round(hotel.match_score * 100)}% match
                  </span>
                  {Array.isArray(hotel.match_reasons) && hotel.match_reasons.length > 0 && (
                    <span className="hc-match-pill__reason">· {hotel.match_reasons[0]}</span>
                  )}
                </div>
              )}
              {hotel.excluded && hotel.exclusion_reason && (
                <div
                  className="hc-match-excluded"
                  title={hotel.exclusion_reason}
                  aria-label={`Excluded: ${hotel.exclusion_reason}`}
                >
                  ⚠ {hotel.exclusion_reason}
                </div>
              )}
            </div>

            {displayGuestRating && (
              <div className="score-box">
                <div className="score-n">{displayGuestRating.toFixed(1)}</div>
                <div className="score-l">
                  {getGuestRatingLabel(displayGuestRating)}
                </div>
              </div>
            )}
          </div>

          {perkChips.length > 0 && (
            <div className="hc-perks">
              {perkChips.map((chip) => (
                <span key={chip} className="hc-perk">{chip}</span>
              ))}
              {remainingCount > 0 && (
                <span className="hc-perk" style={{ opacity: 0.7 }}>+{remainingCount} more</span>
              )}
            </div>
          )}

          {policyBadges.lowStock !== null && (
            <div className="lbl" style={{ color: 'var(--voy-mint)' }}>
              Only {policyBadges.lowStock} left
            </div>
          )}

          {/* Non-refundable disclosure (re-added per VOY-488 review).
              Surfaces only when the room policy is explicitly
              non-refundable AND not already covered by a free-cancel
              chip in `.hc-perks`. Important booking-decision signal. */}
          {policyBadges.nonRefundable && !policyBadges.freeCancellation && (
            <div className="lbl" style={{ color: 'var(--v6-fg-muted)' }}>
              Non-refundable
            </div>
          )}

          <div className="hc-foot">
            {pricing ? (
              <div className="price-pair">
                {showSavings && <span className="price-std">{fmtMoney(pricing.standardPrice)}</span>}
                <span className="price-mem">{fmtMoney(pricing.specialPrice)}</span>
                <span className="lbl" style={{ marginLeft: 6 }}>/ NIGHT</span>
              </div>
            ) : (
              <div className="lbl">Check rates</div>
            )}
            <button type="button" className="hc-cta" onClick={handlePerNightCtaClick}>
              View deal
            </button>
          </div>

          {showSavings && pricing && (
            <div className="save-tag">
              − {fmtMoney(pricing.savings)} as member{cancelSuffix}
            </div>
          )}
        </div>
      </article>

      {showRoomModal && checkin && checkout && guests && (
        <RoomSelectionModal
          hotel={hotel}
          isOpen={showRoomModal}
          onClose={() => setShowRoomModal(false)}
          onSelectRoom={(room: HotelRoomOption, priceType?: 'standard' | 'special', pricing?: PricingResult) => {
            const checkinDate = new Date(checkin!);
            const checkoutDate = new Date(checkout!);
            const nights = Math.ceil((checkoutDate.getTime() - checkinDate.getTime()) / (1000 * 60 * 60 * 24));
            let finalPricePerNight = room.price;
            if (pricing) {
              finalPricePerNight = priceType === 'special' ? pricing.specialPrice : pricing.standardPrice;
            }
            const totalPrice = finalPricePerNight * nights;
            const hotelWithRoom = {
              ...hotel,
              selectedRoom: room,
              price: { amount: finalPricePerNight, currency: room.currency || 'USD' },
              pricePerNight: finalPricePerNight,
              totalPrice: totalPrice,
              nights: nights,
              priceType: priceType,
              checkin: checkin,
              checkout: checkout,
              guests: guests
            };
            addToCart(hotelWithRoom, 'hotel');
            setShowRoomModal(false);
          }}
          checkin={checkin}
          checkout={checkout}
          guests={guests}
        />
      )}
    </>
  )
}
